import React from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import { Box, CircularProgress, Skeleton, useMediaQuery } from '@mui/material';
import LandingPage from './LandingPage';
import AuthForm from './Login';
import Dashboard from './Dashboard';
import PrayersPage from './PrayersPage';
import Header from './Header';
import BottomNav from './BottomNav';
import { useAuth } from '../hooks/useAuth';
import EmailConfirmation from './EmailConfirmation';
import ConfirmationPending from './ConfirmationPending';
import ResendConfirmation from './ResendConfirmation';
import UserProfile from './UserProfile';
import PrayerCircles from './PrayerCircles';
import AdminDashboard from './AdminDashboard';
import SearchPrayers from './SearchPrayers';
import Messages from './Messages';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';
import CircleDetail from './CircleDetail';
import CreatePrayer from './CreatePrayer';

const ContentWrapper = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
}));

const MainContent = styled('main')(({ theme }) => ({
  flex: 1,
  paddingTop: theme.spacing(8), // Space for fixed header
  minHeight: '100vh',
  // Add padding bottom for mobile bottom nav
  [theme.breakpoints.down('md')]: {
    paddingBottom: 80, // Space for bottom navigation
  },
}));

function AppContent() {
  const { isAuthenticated, isUnconfirmed, loading } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  return (
    <ContentWrapper>
      <Header /> 
      <MainContent>
        {loading ? (
          <Box 
            sx={{
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column'
            }}
          >
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Box sx={{ width: '80%', mt: 2 }}>
              <Skeleton variant="rectangular" height={100} />
              <Skeleton variant="text" sx={{ mt: 2 }} />
              <Skeleton variant="text" />
            </Box>
          </Box>
        ) : (
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={
              isAuthenticated ? <Navigate to="/dashboard" /> : 
              <AuthForm isUnconfirmed={isUnconfirmed} />
            } />
            <Route path="/dashboard" element={
              isAuthenticated ? <Dashboard /> : <Navigate to="/login" />
            } />
            <Route path="/prayers" element={
              isAuthenticated ? <PrayersPage /> : <Navigate to="/login" />
            } />
            <Route path="/confirm/:token" element={<EmailConfirmation />} />
            <Route path="/confirmation-pending" element={<ConfirmationPending />} />
            <Route path="/resend-confirmation" element={<ResendConfirmation />} />
            <Route path="/profile" element={
              isAuthenticated ? <UserProfile /> : <Navigate to="/login" />
            } />
            <Route path="/circles" element={
              isAuthenticated ? <PrayerCircles /> : <Navigate to="/login" />
            } />
            <Route path="/circles/:circleId" element={
              isAuthenticated ? <CircleDetail /> : <Navigate to="/login" />
            } />
            <Route path="/search" element={
              isAuthenticated ? <SearchPrayers /> : <Navigate to="/login" />
            } />
            <Route path="/admin" element={
              isAuthenticated ? <AdminDashboard /> : <Navigate to="/login" />
            } />
            <Route path="/messages" element={
              isAuthenticated ? <Messages /> : <Navigate to="/login" />
            } />
            <Route path="/create" element={
              isAuthenticated ? <CreatePrayer /> : <Navigate to="/login" />
            } />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
          </Routes>
        )}
      </MainContent>

      {/* Bottom Navigation for Mobile */}
      {isAuthenticated && <BottomNav />}
    </ContentWrapper>
  );
}

export default AppContent;